import { InputComponent } from "components/components/Component";
import { InputBaseDefinition, InputBaseComponent } from "components/input/baseInputDefinition";
import { OrginalValue } from "components/input/baseHTMLElementInput";
import { ComponentActionHelper } from "components/events/componentActionHelper";
import { fireDataChangedEvent } from "components/form/dataChangedEventDetail";
import { FocusManager } from "components/pages/focusManager";
import i18n from "components/i18n/i18n";
import isEqual from "lodash/isEqual";
import $ from "jquery";
import IMask from "imask";
import template from "./multiText.hbs";
import "./multiText.scss";

export class MultiTextInput extends InputBaseComponent<MultiTextInputDefinition> implements InputComponent {

    public readonly componentType: string = "multiTextInput";

    private _initialValue: OrginalValue = new OrginalValue();
    private _input: MultiTextInputInputHTML[];
    private _visited: boolean = false;

    private _valid: boolean;
    private _validationMessage: any;

    constructor(schema: MultiTextInputDefinition) {
        super(schema);
    }

    public renderTemplate(): Element {
        this.setNoshrinkAttribute();
        return super.renderTemplate(template);
    }

    private setNoshrinkAttribute() {
        for (const input of this.definition?.inputs) {
            if (input.maxlength != null && input.maxlength < 6) {
                input.noshrink = true;
            }
        }
    }

    public render(): Element {
        this.renderTemplate();
        this._input = this.getInputHTMLElements();
        this.initializeMasks();
        this.bindEvents();
        ComponentActionHelper.register(this);
        return this.view;
    }

    private getInputHTMLElements(): MultiTextInputInputHTML[] {
        var arr = new Array<MultiTextInputInputHTML>();
        const inputHTML: HTMLInputElement[] = Array.from(this.view.querySelectorAll("input"));
        var definitionInput = this.definition.inputs.filter((a) => a.label == null);
        for (var i = 0; i < inputHTML.length; i++) {
            arr.push({
                input: inputHTML[i] as HTMLInputElement,
                definition: definitionInput[i] as MultiTextInputInput
            });
        }
        return arr;
    }

    private initializeMasks(): void {
        this._input.forEach(multiTextInputInputHTML => {
            if (multiTextInputInputHTML.definition.allowedPattern != null && multiTextInputInputHTML.definition.allowedPattern.toString() !== "") {
                multiTextInputInputHTML.mask = this.initMaskRegExp(multiTextInputInputHTML.input, multiTextInputInputHTML.definition);
            } else if (multiTextInputInputHTML.definition.numericCode) {
                multiTextInputInputHTML.mask = this.initMaskNumeric(multiTextInputInputHTML.input, multiTextInputInputHTML.definition);
            }
        });
    }

    private initMaskNumeric(input, definition: any): IMask.InputMask<IMask.MaskedNumberOptions> {
        const maskOptions = {
            scale: 0,
            mask: Number,
            min: 0,
            signed: false,
        } as any;

        if (definition.maxlength != null) {
            maskOptions.max = (10 ** definition.maxlength) - 1;
        }

        return IMask(input, maskOptions);
    }

    private initMaskRegExp(input, definition: any): IMask.InputMask<IMask.MaskedRegExp> {
        const maskOptions = {
            mask: new RegExp(definition.allowedPattern),
        } as any;
        return IMask(input, maskOptions);
    }


    private bindEvents(): void {

        this._input.forEach(multiTextInputInputHTML => {
            multiTextInputInputHTML.input.addEventListener("change", () => {
                const data = this.getValue();
                const isEqual = this.isEqualToInitialValue(data);
                if (!isEqual) {
                    this.initialValue.reassignvalue(data);
                    fireDataChangedEvent(this, this.view, this.getValue());
                }
            });

            multiTextInputInputHTML.input.addEventListener("focus", (evt) => {
                this._visited = true;
                evt.preventDefault();
            });

            if (multiTextInputInputHTML.definition.tabOnEnter === true) {
                multiTextInputInputHTML.input.addEventListener("keydown", (e) => {
                    if (e.key === "Enter") {
                        e.stopPropagation();
                        e.preventDefault();
                        FocusManager.focusNext();
                    }
                });
            }

            /** Currently the functionality is not being used, so we comment it to simplify component
             * If you want to use it in the future, uncomment and optimize
            if (multiTextInputInputHTML.definition.disableFinishedTyping != null && multiTextInputInputHTML.definition.disableFinishedTyping === true) {
                multiTextInputInputHTML.input.addEventListener("keyup", () => {
                    const data = this.getValue();
                    const isEqual = this.isEqualToInitialValue(data);
                    if (!isEqual) {
                        let doneTyping = () => this.view.dispatchEvent(new Event("change"));
                        clearTimeout(this.typingTimer);
                        this.typingTimer = window.setTimeout(doneTyping, SimpleInputBaseComponent.TIMEOUT_FINISHEDTYPING);
                    }
                });
            }
             */
        });

        this.view.addEventListener("focusout", () => {
            this.setValidationStatus(this._valid, this._validationMessage);
        });
    }

    public getValue(): Array<string | number> | any {
        var obj = {};
        this._input.forEach(multiTextInputInputHTML => {
            let text = multiTextInputInputHTML.input.value;
            if (multiTextInputInputHTML.definition.numericCode === true) {
                let number = parseInt(text, 10);
                if (isNaN(number)) {
                    return null;
                }
                obj[multiTextInputInputHTML.definition.id] = number;
            } else {
                obj[multiTextInputInputHTML.definition.id] = ((multiTextInputInputHTML.definition.uppercase) ? text.toUpperCase() : text);
            }
        });

        //Check if there one field filled, otherwise is null;
        let isAnyFilled: boolean = false;
        for (let value of Object.values(obj)) {
            if (value != null && value !== "") {
                isAnyFilled = true;
                break;
            }
        }
        if (!isAnyFilled) {
            return null;
        }

        return obj;
    }

    public value(v: any) {
        this.assignValuesToHTMLInputElement(v);
        this._initialValue.value = this.getValue();
    }

    private assignValuesToHTMLInputElement(v: any) {
        if (v == null || typeof v !== "object") {
            this._input.forEach(multiTextInputInputHTML => {
                multiTextInputInputHTML.input.value = null;
                if (multiTextInputInputHTML.mask) {
                    multiTextInputInputHTML.mask?.updateValue();
                }
            });
        } else if (typeof v === "object") {
            this._input.forEach(multiTextInputInputHTML => {
                let text;
                if (multiTextInputInputHTML.definition.translate) {
                    text = i18n.t(v[multiTextInputInputHTML.definition.id]);
                } else {
                    text = v[multiTextInputInputHTML.definition.id];
                }
                multiTextInputInputHTML.input.value = ((multiTextInputInputHTML.definition.uppercase) ? text.toUpperCase() : text);
                if (multiTextInputInputHTML.mask) {
                    multiTextInputInputHTML.mask?.updateValue();
                }
            });
        }
    }

    public setReadOnly(ro: boolean) {
        this._input.forEach(multiTextInputInputHTML => {
            multiTextInputInputHTML.input.readOnly = ro;
        });
    }

    public setRequired(required: boolean) {
        if (required) {
            $(this.view).find("label").addClass("is-required");
        } else {
            $(this.view).find("label").removeClass("is-required");
        }
    }

    public setValidationStatus(valid: boolean, message?: string | any) {
        this._valid = valid ?? true;
        this._validationMessage = message || null;

        if (this._valid) {
            $(this.view).find("label").removeClass("is-invalid");
        } else {
            $(this.view).find("label").addClass("is-invalid");
        }
        const errorClass = this._visited ? "has-error" : "is-pending";
        if (typeof message === "string" || message instanceof String) {
            let input: HTMLInputElement = this.view.querySelector("input");
            let element: Element = this.view.getElementsByClassName("o-form-component__input-group")[0];
            if (this._valid) {
                input.classList.remove("is-pending");
                input.classList.remove("has-error");
                element.removeAttribute("data-tip");
            } else {
                input.classList.add(errorClass);
                element.setAttribute("data-tip", message as string);
            }
        } else {
            let multiMessage;
            this._input.forEach(multiTextInputInputHTML => {
                multiTextInputInputHTML.input.classList.remove("has-error");
                multiTextInputInputHTML.input.classList.remove("is-pending");
            });
            for (const key in message as any) {
                if (message.hasOwnProperty(key)) {
                    const element = message[key];
                    if (!this._valid) {
                        if ("0" === key) {
                            this._input.forEach(multiTextInputInputHTML => {
                                multiTextInputInputHTML.input.classList.add(errorClass);
                            });
                        } else {
                            const input = this.view.querySelector("#mti-" + this.definition.id + "-" + key);
                            if (input != null) {
                                input.classList.add(errorClass);
                            }
                        }
                        if (element != null) {
                            multiMessage = (multiMessage ? multiMessage + " / " : "") + element;
                        }
                    }
                }
            }
            let element: Element = this.view.getElementsByClassName("o-form-component__input-group")[0];
            if (multiMessage == null) {
                element.removeAttribute("data-tip");
            } else {
                element.setAttribute("data-tip", multiMessage as string);
            }
        }
    }

    private isEqualToInitialValue(data: any): boolean {
        let isequal: boolean = true;
        const initialValue = this.initialValue.value;

        isequal = isEqual(this.getValue(), initialValue);

        return isequal;
    }

    public get initialValue() {
        return this._initialValue;
    }
}

export interface MultiTextInputDefinition extends InputBaseDefinition {
    inputs: MultiTextInputInput[];
}

export interface MultiTextInputInput {
    grow?: number;
    id?: string;
    label?: string;
    noshrink?: boolean;
    maxlength?: number;
    numericCode?: boolean;
    translate?: boolean;
    uppercase?: boolean;
    allowedPattern?: RegExp;
    tabOnEnter?: boolean;
    disableFocusKeyboard?: boolean;
    //disableFinishedTyping?: boolean;
}

interface MultiTextInputInputHTML {
    input: HTMLInputElement,
    definition: MultiTextInputInput
    mask?: IMask.InputMask<IMask.MaskedNumberOptions | IMask.MaskedRegExp>,
}
